import * as React from "react";
import {OnMounted}      from "@Common/react/on-mounted";

import "./fieldOptions.scss";

import {cs}                      from "@Common/react/chain-services";
import {Load2}                   from "@Common/react/load2";
import {UseState}                from "@Common/react/use-state";
import {fragments}               from "@Common/react/fragments";
import {Effect}                  from "@Common/react/effect";
import {AlertModal}              from "@Common/ui-components/alert-modal/alert-modal";
import {FieldOptionsTools}       from "./fieldOptionsTools";

export const FieldOptions = ({
    reference=null,
    fieldId=null,
    children=null,
    idOrder=null,
    fieldData,
    allowAiTools=null,
    aiOptions=null,
    allowSuggestedComments=null,
    charOverrideModalOptions=null,
    allowVdp=null,
    mergeFieldOptions=null,
    charCount=null,
    maxChars=null,
    maxCharsExceeded=null,
    allowOverride=null,
    overrideCKValue=null,
    overrideCheckHandler=null,
    explainerText=null,
    tinyMceField=false,
    ccField=false,
    isAPM=false,
}) => cs(
    ["allowedTools", ({}, next) => next({
        aiTools: allowAiTools,
        vdp: allowVdp,
        suggestedComments: allowSuggestedComments,
        explainerText: !!explainerText,
    })],
    ["fieldRef", ({}, next) => next( reference && reference.get())],
    ["charCountModal", ({}, next) => AlertModal({
        next,
        header: "Character Count Override",
        message: charOverrideModalOptions?.modalMessage?.value,
        xdQaName: "characterCountModal",
        okCallback: () => {charOverrideModalOptions?.okCallback.value()},
        cancelButton: charOverrideModalOptions?.cancelButton?.value,
        cancelCallback: () => {charOverrideModalOptions?.cancelCallback?.value()}
    })],
    ["fieldHeight", ({}, next) => UseState({
        initValue: 0,
        next
    })],
    ["stickyBottomFrame", ({}, next) => UseState({
        _key: "stickyBottomFrame" + fieldId,
        initValue: false,
        next
    })],
    // Callback function triggered by ResizeObserver below, changes height of UI to match new height of Quill editor
    ["entriesHandler", ({fieldHeight}, next) => next( (entries, resizeObserver) => {
        let lastfieldHeight = fieldHeight.value;
        const newFieldHeight  = entries[0].contentRect.height;
        if (newFieldHeight !== lastfieldHeight) {
            fieldHeight.onChange(newFieldHeight);
        }

        resizeObserver.disconnect;
    })],
    // resizeObserver to watch Quill Editors for changes in height from user input
    ["resizeObserver", ({entriesHandler}, next) => Load2({
        fetch: async () =>  new ResizeObserver((entries, resizeObserver) => entriesHandler(entries, resizeObserver)),
        next,
    })],
    ["getTotalHeightOfChild", ({}, next) => next( (childElem) => {
        const fieldStyles = window.getComputedStyle(childElem);
        const fieldMargins = parseFloat(fieldStyles['marginTop']) + parseFloat(fieldStyles['marginBottom']);
        const totalFieldHeight = childElem?.offsetHeight + fieldMargins;
        return totalFieldHeight;
    })],
    // Needed for CC TextareaAutosize grow in size to fit text/returns
    ({fieldHeight}, next) => fragments(
        next(),
        Effect({
            action: () => {
                const listener = (e) => {
                    if (e.target !== reference.get()) {
                        return;
                    }
                    const heightOfField = reference.get().offsetHeight;
                    heightOfField && fieldHeight.onChange(heightOfField)
                };
                window.addEventListener("autosize:resized", listener);
                return () =>window.removeEventListener("autosize:resized", listener);
            },
        }),
    ),
    ["checkForStickyBottomEligibility", ({stickyBottomFrame}, next) => next( () => {
        const contentContainer = window.document.getElementById("content-container-j43-" + fieldData.idPrimary.toString());
        let rect;

        // Quill Editors
        if (allowVdp && !isAPM) {
            rect = window.document.getElementById(`quill-editor-${fieldId}`)?.getBoundingClientRect();
        }
        // TinyMCE fields
        else if (tinyMceField) {
            const referenceId = reference?.get()?.id;
            const origRefField = window.document.getElementById(referenceId);
            const fieldElem = origRefField?.parentNode as HTMLElement;
            rect = fieldElem?.getBoundingClientRect();
        }
        // Build3 CommentsBoxWithCounter & CC TextFieldAutosize fields
        else {
            rect = reference.get()?.getBoundingClientRect();
        }

        const isBottomBelowView = rect?.bottom > (window.innerHeight - contentContainer?.offsetHeight);
        const topScrollBreakpoint = window.innerHeight - 100;
        const showStickyBottom = isBottomBelowView && rect?.top < topScrollBreakpoint;

        if (showStickyBottom) {
            stickyBottomFrame.onChange(true);
        } else {
            stickyBottomFrame.onChange(false);
        }
    })],
    // scroll event listener to check if field is eligable for stickyBottomFrame based on field's relation to window
    ({checkForStickyBottomEligibility}, next) => fragments(
        next(),
        Effect({
            action: () => {
                window.addEventListener('scroll', checkForStickyBottomEligibility);
                return () => window.removeEventListener("scroll", checkForStickyBottomEligibility);
            },
        }),
    ),

    ({fieldRef, fieldHeight, allowedTools, stickyBottomFrame, resizeObserver, getTotalHeightOfChild, checkForStickyBottomEligibility}) => (<>
        {window.document.getElementById(`quill-editor-${fieldId}`) && resizeObserver?.value?.observe(window.document.getElementById(`quill-editor-${fieldId}`) as HTMLElement)}
        {Object.values(allowedTools).some(value => Boolean(value)) ?
            <div className="field-options-d20"
                id={"field-options-" + fieldData.idPrimary.toString()}
                tabIndex={1}
            >
                {charOverrideModalOptions?.showCharCountModal?.value && <AlertModal
                    initShow={true}
                    next={() => {}}
                    header="Character Count Override"
                    message={charOverrideModalOptions?.modalMessage?.value}
                    xdQaName="characterCountModal"
                    okCallback={() => {charOverrideModalOptions?.okCallback?.value()}}
                    cancelButton={charOverrideModalOptions?.cancelButton?.value}
                    cancelCallback={() => charOverrideModalOptions?.cancelCallback?.value()}
                />}
                {/* set UI frame & space-holder heights to match with child field's total height on component mount */}
                {(fieldRef || ccField)  && OnMounted({
                    action: async () => {
                        // Build3 Fields
                        if (!ccField) {
                            // TinyMCE fields
                            if (tinyMceField) {
                                const referenceId = fieldRef.id;
                                const origRefField = window.document.getElementById(referenceId);
                                const editorElem = origRefField?.parentNode as HTMLElement;
                                const totalEditorHeight = getTotalHeightOfChild(editorElem);
                                totalEditorHeight && fieldHeight.onChange(totalEditorHeight);
                            }
                            // CommentsBoxWithCounter fields
                            else {
                                const referenceId = fieldRef.id;
                                const currRefField = window.document.getElementById(referenceId);
                                const totalFieldHeight = getTotalHeightOfChild(currRefField);
                                totalFieldHeight && fieldHeight.onChange(totalFieldHeight);
                            }
                        }
                        // CC Fields
                        else {
                            // Quill Editors
                            if (allowVdp && !isAPM) {
                                const currRefField = window.document.getElementById(`quill-editor-${fieldId}`);
                                // Remove onFocus glow from editors that will be wrapped in frame with focus
                                currRefField?.style.setProperty("box-shadow", "none");
                                const totalEditorHeight = getTotalHeightOfChild(currRefField);
                                totalEditorHeight && fieldHeight.onChange(totalEditorHeight);
                            }
                            // Regular CC Fields
                            else if (reference.get() && !isAPM) {
                                // Remove onFocus glow from fields that will be wrapped in frame with focus
                                reference.get().style.setProperty("box-shadow", "none");
                                const currRefField = reference.get();
                                const totalFieldHeight = getTotalHeightOfChild(currRefField);
                                totalFieldHeight && fieldHeight.onChange(totalFieldHeight);
                            }
                        }
                        checkForStickyBottomEligibility();
                    }
                })}
                <div className="field-options-ui" id={"field-options-ui-" + fieldData.idPrimary.toString()}>
                    <div className="header-text">
                        {fieldData.displayLabel}
                    </div>
                    <div className={"space-holder"} style={{height: fieldHeight.value + "px"}} />
                    {/* This div extends the ui frame down to meet the tools container so the box-shadow is uniform */}
                    <div style={{
                        height: window.document.getElementById("content-container-j43-" + fieldData.idPrimary.toString())?.offsetHeight + "px"
                    }}/>

                </div>
                <div
                    className="children-div"
                    id={"children-div-" + fieldData.idPrimary.toString()}
                    style={tinyMceField ? {height: fieldHeight.value - 5 + "px"} : {height: fieldHeight.value + "px"}}
                >
                    {children}
                </div>
                    {/* This acts as a bottom border on the field within the frame when we've made the Tools footer sticky */}
                    {<div className={stickyBottomFrame.value ? "simulated-border sticky-border-active" : "simulated-border"}
                        id={"simulated-border-" + fieldData.idPrimary.toString()}
                        style={stickyBottomFrame.value ?
                            {bottom: window.document.getElementById("content-container-j43-" + fieldData.idPrimary.toString())?.offsetHeight - 0.75 + "px"}
                            : {}
                        }
                    />}
                    {/* {stickyBottomFrame.value && <div className="simulated-border"></div>} */}
                    <FieldOptionsTools
                        stickyBottomActive={stickyBottomFrame.value}
                        idOrder={idOrder}
                        tinyMceField={tinyMceField}
                        ccField={ccField}
                        fieldData={fieldData}
                        aiOptions={aiOptions}
                        charOverrideModalOptions={charOverrideModalOptions}
                        allowSuggestedComments={allowSuggestedComments}
                        allowVdp={allowVdp}
                        mergeFieldOptions={mergeFieldOptions}
                        charCount={charCount}
                        maxChars={maxChars}
                        maxCharsExceeded={maxCharsExceeded}
                        allowOverride={allowOverride}
                        overrideCKValue={overrideCKValue}
                        overrideCheckHandler={overrideCheckHandler}
                        explainerText={explainerText}
                    />
            </div>
        : children}
    </>)
);